import { useMemo } from 'react';
import { IconBuildingStore } from '@tabler/icons-react';

import { StackedBarChart } from '@charts';

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function pad(n) {
    return String(n).padStart(2, '0');
}

function bucketLabel(point, granularityType) {
    if (!point?.date?.from) return '';
    const d = new Date(point.date.from.replace(' ', 'T'));
    switch (granularityType) {
        case 'hour':
            return `${pad(d.getHours())}h`;
        case 'day':
        case 'week':
            return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
        case 'month':
            return `${MONTH_LABELS[d.getMonth()]}/${String(d.getFullYear()).slice(-2)}`;
        case 'year':
            return String(d.getFullYear());
        default:
            return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}`;
    }
}

export default function ProductRevenueChart({ report, reportLoading, plans }) {
    const productRevenue = report?.productRevenue;
    const granularityType = productRevenue?.granularity?.type || 'day';
    const points = productRevenue?.data || [];

    const { series, labels } = useMemo(() => {
        if (!points.length) return { series: [], labels: [] };

        // productRevenue keys arrive as strings (JSON object keys); plan.id from
        // the loaded plans list is an integer — stringify both sides.
        const planLabelById = new Map(
            (plans || []).map(plan => [String(plan.id), plan.label])
        );

        const totals = new Map();
        for (const point of points) {
            const value = point?.value || {};
            for (const planId of Object.keys(value)) {
                const amount = Number(value[planId]?.amount) || 0;
                totals.set(planId, (totals.get(planId) || 0) + amount);
            }
        }

        const planIds = Array.from(totals.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([id]) => id);

        const labels = points.map(p => bucketLabel(p, granularityType));

        const series = planIds.map(planId => ({
            id: planId,
            label: planLabelById.get(planId) || `Plano ${planId}`,
            data: points.map(point => {
                const stat = point?.value?.[planId];
                return stat ? Number(stat.amount) / 100 : 0;
            }),
        }));

        return { series, labels };
    }, [points, granularityType, plans]);

    return (
        <StackedBarChart
            title="Receita por plano"
            icon={<IconBuildingStore size={15} />}
            series={series}
            labels={labels}
            valueType="money"
            report={report}
            reportLoading={reportLoading}
        />
    );
}
